import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  DOCUMENT_SEARCH_BASE_PATH,
  DOCUMENT_SEARCH_BROWSE_BASE,
  DOCUMENT_SEARCH_GROUP_PARAM,
  DOCUMENT_SEARCH_PATH_PARAM,
  buildDocumentSearchBrowsePathname,
  buildDocumentSearchSearchString,
  decodeFolderPath,
  encodeFolderPath,
  folderPathsEqual,
  isDocumentSearchBrowsePath,
} from "../utils/documentSearchNavigation";

export interface NavigateFolderPathOptions {
  /** When true, replaces the current history entry instead of pushing a new one. */
  replace?: boolean;
  /** Aircraft tail number when navigating inside grouped (multi-aircraft) results. */
  group?: string | null;
}

function folderPathFromPathname(pathname: string): string[] {
  if (!isDocumentSearchBrowsePath(pathname)) {
    return [];
  }

  if (pathname === DOCUMENT_SEARCH_BROWSE_BASE) {
    return [];
  }

  const prefix = `${DOCUMENT_SEARCH_BROWSE_BASE}/`;
  if (!pathname.startsWith(prefix)) {
    return [];
  }

  return decodeFolderPath(pathname.slice(prefix.length));
}

/**
 * Keeps document-search folder depth in the URL under `/document/search/browse/...`
 * so browser Back/Forward moves between folders without touching
 * `/document/search/:id` (document viewer).
 */
export function useDocumentSearchFolderNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const folderPath = useMemo(() => {
    const fromBrowse = folderPathFromPathname(location.pathname);
    if (fromBrowse.length > 0 || isDocumentSearchBrowsePath(location.pathname)) {
      return fromBrowse;
    }

    // Legacy bookmarks: /document/search?path=...
    return decodeFolderPath(searchParams.get(DOCUMENT_SEARCH_PATH_PARAM));
  }, [location.pathname, searchParams]);

  const folderGroup = searchParams.get(DOCUMENT_SEARCH_GROUP_PARAM);

  // One-time migrate legacy ?path= bookmarks onto /browse/...
  useEffect(() => {
    const legacyPath = searchParams.get(DOCUMENT_SEARCH_PATH_PARAM);
    if (!legacyPath) {
      return;
    }

    if (isDocumentSearchBrowsePath(location.pathname)) {
      return;
    }

    const path = decodeFolderPath(legacyPath);
    const group = searchParams.get(DOCUMENT_SEARCH_GROUP_PARAM);

    navigate(
      {
        pathname: buildDocumentSearchBrowsePathname(path),
        search: buildDocumentSearchSearchString(location.search, { group }),
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, searchParams]);

  const navigateFolderPath = useCallback(
    (path: string[], options?: NavigateFolderPathOptions) => {
      const replace = options?.replace === true;
      const group =
        options && Object.prototype.hasOwnProperty.call(options, "group")
          ? options.group ?? null
          : folderGroup;

      const nextPathname = buildDocumentSearchBrowsePathname(path);
      const nextSearch = buildDocumentSearchSearchString(location.search, {
        group,
      });

      if (
        folderPathsEqual(folderPath, path) &&
        location.pathname === nextPathname &&
        (location.search || "") === nextSearch
      ) {
        return;
      }

      navigate(
        {
          pathname: nextPathname,
          search: nextSearch,
        },
        { replace }
      );
    },
    [folderGroup, folderPath, location.pathname, location.search, navigate]
  );

  const clearFolderNavigation = useCallback(
    (options?: { replace?: boolean }) => {
      const replace = options?.replace !== false;
      const nextSearch = buildDocumentSearchSearchString(location.search, {
        group: null,
      });

      if (
        location.pathname === DOCUMENT_SEARCH_BASE_PATH &&
        !(location.search || "").includes(`${DOCUMENT_SEARCH_PATH_PARAM}=`) &&
        !(location.search || "").includes(`${DOCUMENT_SEARCH_GROUP_PARAM}=`) &&
        (location.search || "") === nextSearch
      ) {
        return;
      }

      navigate(
        {
          pathname: DOCUMENT_SEARCH_BASE_PATH,
          search: nextSearch,
        },
        { replace }
      );
    },
    [location.pathname, location.search, navigate]
  );

  return {
    folderPath,
    folderGroup,
    navigateFolderPath,
    clearFolderNavigation,
    /** Exposed for tests / debugging */
    encodeFolderPath,
  };
}
